import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { format, subMonths, startOfMonth, endOfMonth } from 'date-fns';
import { Calendar, Download, Loader2, Printer, CreditCard } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { fetchPartyStatement } from '@/app/services/reportService';
import { getParty } from '@/app/services/partyService';
import { PartyStatementResponse } from '@/app/types/reports';
import { OnAccountPaymentModal } from './OnAccountPaymentModal';

interface PartyStatementProps {
  partyId: number;
  partyName?: string;
}

export default function PartyStatement({ partyId, partyName }: PartyStatementProps) {
  const today = new Date();
  const [dateRange, setDateRange] = useState({
    startDate: format(startOfMonth(today), 'yyyy-MM-dd'),
    endDate: format(endOfMonth(today), 'yyyy-MM-dd'),
  });

  const [selectedPreset, setSelectedPreset] = useState('current-month');
  const [showOnAccountPaymentModal, setShowOnAccountPaymentModal] = useState(false);

  // Fetch party details
  const { data: party } = useQuery({
    queryKey: ['party', partyId],
    queryFn: () => getParty(partyId),
    enabled: Boolean(partyId),
  });

  const { data: statement, isLoading, error } = useQuery({
    queryKey: ['party-statement', partyId, dateRange.startDate, dateRange.endDate],
    queryFn: () => fetchPartyStatement({
      party_id: partyId,
      start_date: dateRange.startDate,
      end_date: dateRange.endDate,
    }),
    enabled: Boolean(partyId),
  });

  const handlePresetChange = (preset: string) => {
    setSelectedPreset(preset);
    const today = new Date();
    let startDate: Date;
    let endDate: Date;

    switch (preset) {
      case 'current-month':
        startDate = startOfMonth(today);
        endDate = endOfMonth(today);
        break;
      case 'last-month':
        const lastMonth = subMonths(today, 1);
        startDate = startOfMonth(lastMonth);
        endDate = endOfMonth(lastMonth);
        break;
      case 'last-3-months':
        startDate = startOfMonth(subMonths(today, 2));
        endDate = endOfMonth(today);
        break;
      case 'last-6-months':
        startDate = startOfMonth(subMonths(today, 5));
        endDate = endOfMonth(today);
        break;
      case 'current-year':
        startDate = new Date(today.getFullYear(), 0, 1);
        endDate = endOfMonth(today);
        break;
      default:
        return;
    }

    setDateRange({
      startDate: format(startDate, 'yyyy-MM-dd'),
      endDate: format(endDate, 'yyyy-MM-dd'),
    });
  };

  const handlePrint = () => {
    window.print();
  };

  const handleExport = () => {
    if (!statement) return;
    
    // Create CSV content
    const headers = ['Date', 'Transaction Type', 'Invoice/Bill No', 'Debit', 'Credit', 'Balance'];
    const rows = statement.entries.map(entry => [
      format(new Date(entry.date), 'dd/MM/yyyy'),
      entry.txn_type,
      entry.invoice_bill_no || '',
      entry.debit,
      entry.credit,
      entry.running_balance,
    ]);

    const csvContent = [
      `Party Statement - ${statement.party_name}`,
      `Period: ${format(new Date(statement.start_date), 'dd/MM/yyyy')} to ${format(new Date(statement.end_date), 'dd/MM/yyyy')}`,
      '',
      headers.join(','),
      ...rows.map(row => row.join(',')),
      '',
      `Total Debit,,,${statement.total_debit},,`,
      `Total Credit,,,,${statement.total_credit},`,
      `Closing Balance,,,,,${statement.closing_balance} ${statement.closing_balance_type}`,
    ].join('\n');

    // Download CSV
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `party-statement-${statement.party_name.replace(/\s+/g, '-')}-${dateRange.startDate}-to-${dateRange.endDate}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  const formatAmount = (amount: string) => {
    const num = parseFloat(amount);
    return num.toLocaleString('en-IN', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  return (
    <div className="space-y-6">
      {/* Actions */}
      <div className="flex justify-end">
        <Button onClick={() => setShowOnAccountPaymentModal(true)} disabled>
          <CreditCard className="mr-2 h-4 w-4" />
          Adjust Account
        </Button>
      </div>

      {/* Date Range Selector */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Statement Period</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <Label htmlFor="preset">Quick Select</Label>
              <Select value={selectedPreset} onValueChange={handlePresetChange}>
                <SelectTrigger>
                  <SelectValue placeholder="Select period" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="current-month">Current Month</SelectItem>
                  <SelectItem value="last-month">Last Month</SelectItem>
                  <SelectItem value="last-3-months">Last 3 Months</SelectItem>
                  <SelectItem value="last-6-months">Last 6 Months</SelectItem>
                  <SelectItem value="current-year">Current Year</SelectItem>
                  <SelectItem value="custom">Custom</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="start-date">Start Date</Label>
              <Input
                id="start-date"
                type="date"
                value={dateRange.startDate}
                onChange={(e) => {
                  setDateRange(prev => ({ ...prev, startDate: e.target.value }));
                  setSelectedPreset('custom');
                }}
              />
            </div>
            <div>
              <Label htmlFor="end-date">End Date</Label>
              <Input
                id="end-date"
                type="date"
                value={dateRange.endDate}
                onChange={(e) => {
                  setDateRange(prev => ({ ...prev, endDate: e.target.value }));
                  setSelectedPreset('custom');
                }}
              />
            </div>
            <div className="flex items-end gap-2">
              <Button
                variant="outline"
                size="icon"
                onClick={handlePrint}
                disabled={!statement || statement.entries.length === 0}
              >
                <Printer className="h-4 w-4" />
              </Button>
              <Button
                variant="outline"
                size="icon"
                onClick={handleExport}
                disabled={!statement || statement.entries.length === 0}
              >
                <Download className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Statement Display */}
      {isLoading ? (
        <div className="flex justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin" />
        </div>
      ) : error ? (
        <Alert variant="destructive">
          <AlertDescription>
            Failed to load statement: {(error as any).message}
          </AlertDescription>
        </Alert>
      ) : statement && statement.entries.length > 0 ? (
        <Card className="print:shadow-none">
          <CardContent className="p-6">
            {/* Header Information */}
            <div className="mb-6 print:mb-8">
              <h2 className="text-2xl font-bold mb-2">Party Statement</h2>
              <div className="border-b pb-4 mb-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <p className="font-semibold">{statement.party_name}</p>
                    {statement.contact_no && <p className="text-sm text-muted-foreground">Contact: {statement.contact_no}</p>}
                    {statement.address && <p className="text-sm text-muted-foreground">{statement.address}</p>}
                    {statement.gstin && <p className="text-sm text-muted-foreground">GSTIN: {statement.gstin}</p>}
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-muted-foreground">
                      Period: {format(new Date(statement.start_date), 'dd/MM/yyyy')} to {format(new Date(statement.end_date), 'dd/MM/yyyy')}
                    </p>
                    {party?.unallocated_credit && Number(party.unallocated_credit) > 0 && (
                      <p className="text-sm font-medium text-orange-600 mt-1">
                        Unallocated Credit: ₹{parseFloat(party.unallocated_credit.toString()).toFixed(2)}
                      </p>
                    )}
                    {party?.unallocated_debit && Number(party.unallocated_debit) > 0 && (
                      <p className="text-sm font-medium text-orange-600 mt-1">
                        Unallocated Debit: ₹{parseFloat(party.unallocated_debit.toString()).toFixed(2)}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Statement Table */}
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-2 px-2">Date</th>
                    <th className="text-left py-2 px-2">Transaction Type</th>
                    <th className="text-left py-2 px-2">Invoice No.</th>
                    <th className="text-right py-2 px-2">Debit</th>
                    <th className="text-right py-2 px-2">Credit</th>
                    <th className="text-right py-2 px-2">Balance</th>
                  </tr>
                </thead>
                <tbody>
                  {statement.entries.map((entry, index) => (
                    <tr key={index} className="border-b">
                      <td className="py-2 px-2">{format(new Date(entry.date), 'dd/MM/yyyy')}</td>
                      <td className="py-2 px-2">{entry.txn_type}</td>
                      <td className="py-2 px-2">{entry.invoice_bill_no || '-'}</td>
                      <td className="text-right py-2 px-2">
                        {parseFloat(entry.debit) > 0 ? `₹ ${formatAmount(entry.debit)}` : ''}
                      </td>
                      <td className="text-right py-2 px-2">
                        {parseFloat(entry.credit) > 0 ? `₹ ${formatAmount(entry.credit)}` : ''}
                      </td>
                      <td className="text-right py-2 px-2">
                        ₹ {formatAmount(entry.running_balance)}
                      </td>
                    </tr>
                  ))}
                  {/* Totals Row */}
                  <tr className="font-semibold border-t-2">
                    <td colSpan={3} className="py-2 px-2">Total:</td>
                    <td className="text-right py-2 px-2">₹ {formatAmount(statement.total_debit)}</td>
                    <td className="text-right py-2 px-2">₹ {formatAmount(statement.total_credit)}</td>
                    <td className="text-right py-2 px-2"></td>
                  </tr>
                  {/* Closing Balance Row */}
                  <tr className="font-semibold">
                    <td colSpan={5} className="py-2 px-2 text-right">Closing Balance:</td>
                    <td className="text-right py-2 px-2">
                      ₹ {formatAmount(statement.closing_balance)} ({statement.closing_balance_type})
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      ) : (
        <Alert>
          <AlertDescription>No transactions found for the selected period.</AlertDescription>
        </Alert>
      )}

      {/* Print Styles */}
      <style jsx>{`
        @media print {
          @page {
            margin: 20mm;
          }
          
          body {
            print-color-adjust: exact;
            -webkit-print-color-adjust: exact;
          }
          
          .print\\:shadow-none {
            box-shadow: none !important;
          }
          
          .print\\:mb-8 {
            margin-bottom: 2rem !important;
          }
        }
      `}</style>

      {/* On-Account Payment Modal */}
      <OnAccountPaymentModal
        isOpen={showOnAccountPaymentModal}
        onClose={() => setShowOnAccountPaymentModal(false)}
        party={party}
      />
    </div>
  );
}